import { FC, createContext, useContext, useState, useMemo } from 'react';

import { PlanFeaturesTypes } from '~globals/types/enums';
import { useAppSelector } from '~hooks/useAppSelector';
import { useTypePartnerCompany } from '~hooks/useTypePartnerCompany';
import { useSubscriptionFeatures } from '~hooks/useSubscriptionFeatures';
import { useRequest } from '~hooks/useRequest';
import { selectAuth } from '~store/slices/auth/index';
import { getAssociatedCompanies } from '~services/partnerCompany';
import { AuthExtendedCompany } from '~services/auth/types';
import { getFeatureAvailableToDate } from '~utils/feature';
import { isOnlyCreatorOrder } from '~utils/user';

import {
  DialogImportOrdersFromCSVContextValue,
  DialogImportOrdersFromCSVProps,
} from './types';

const DialogImportOrdersFromCSVContext =
  createContext<DialogImportOrdersFromCSVContextValue>({
    open: false,
    onClose: () => {},
    onSubmitSuccess: () => {},
    showAssociatedCompanies: false,
    associatedCompanies: {
      list: [],
      loading: true,
    },
    onUpdateErrors: undefined,
  });

export const useDialogImportOrdersFromCSVContext =
  (): DialogImportOrdersFromCSVContextValue =>
    useContext(DialogImportOrdersFromCSVContext);

const DialogImportOrdersFromCSVProvider: FC<DialogImportOrdersFromCSVProps> = ({
  open,
  onClose,
  onSubmitSuccess,
  onUpdateErrors,
  enabledFetchAssociatedCompanies = false,
  children,
}) => {
  const { user } = useAppSelector(selectAuth);
  const { isCarrier } = useTypePartnerCompany();
  const { hasAccessToFeature } = useSubscriptionFeatures();

  const hasAccessMgmGiversModules = useMemo(
    () =>
      hasAccessToFeature(
        PlanFeaturesTypes.MgmGiversModule,
        getFeatureAvailableToDate(PlanFeaturesTypes.MgmGiversModule),
      ),
    [hasAccessToFeature],
  );

  const isUserOnlyCreatorOrder = useMemo(
    () => isOnlyCreatorOrder(user),
    [user],
  );

  const showAssociatedCompanies = useMemo(() => {
    if (!hasAccessMgmGiversModules) return false;

    return (
      enabledFetchAssociatedCompanies && isCarrier && !isUserOnlyCreatorOrder
    );
  }, [
    hasAccessMgmGiversModules,
    enabledFetchAssociatedCompanies,
    isCarrier,
    isUserOnlyCreatorOrder,
  ]);

  const [associatedCompanies, setAssociatedCompanies] = useState<
    AuthExtendedCompany[]
  >([]);

  const [, loadingAssocitedCompanies] = useRequest({
    initialLoading: showAssociatedCompanies,
    isValidToRequest: showAssociatedCompanies,
    request: getAssociatedCompanies,
    payload: null,
    withPostSuccess: (response) => {
      const associatedCompaniesResponse = response.data?.data
        .results as AuthExtendedCompany[];

      associatedCompaniesResponse
        // Alfabethic
        .sort((a, b) => a.name.localeCompare(b.name))
        // Current Company first
        .sort((a, b) =>
          a.id === user?.companyId ? -1 : b.id === user?.companyId ? 1 : 0,
        );

      setAssociatedCompanies(associatedCompaniesResponse);
    },
  });

  return (
    <DialogImportOrdersFromCSVContext.Provider
      value={{
        open,
        onClose,
        onSubmitSuccess,
        showAssociatedCompanies,
        associatedCompanies: {
          list: associatedCompanies,
          loading: loadingAssocitedCompanies,
        },
        onUpdateErrors,
      }}
    >
      {children}
    </DialogImportOrdersFromCSVContext.Provider>
  );
};

export default DialogImportOrdersFromCSVProvider;
